import * as Yup from 'yup';
import { ENABLE_MF_TRANSACTIONS_POA_DOCUMENT_UPLOAD, MPOWER_ACCOUNT_TYPE_MASTER, USER_ROLES } from '../../../../utils/constant';
import dayjs from 'dayjs';
import { handleDateValidation } from '.';
import { isRMLikeRole } from '../../../../utils/utilityFunctions';


export const switchScheme = (currencySymbol: string, role: string, selectedPortfolioObj: Record<string, any>) => Yup.object().shape({
    fund: Yup.lazy(() => {
        return Yup.array().of(
            Yup.object().shape({
                switchValue: Yup
                    .string()
                    .required('Switch Value is required'),
                installment: Yup
                    .mixed()
                    .test('installment-validation', function (value, context) {
                        const { parent, createError, path } = context;

                        if (parent?.switchValue === 'amount') {

                            const outSchValidation = parent?.outgoingSchInvDetails?.validations?.find((item: any) => item?.transactionType === 'RED')

                            const inSchValidation = parent?.incomingSchInvDetails?.validations?.find((item: any) => item?.transactionType === 'RED')

                            const minimumInstallmentAmount = Math.max(Number(outSchValidation?.minimumInstallmentAmountOut ?? 0), Number(inSchValidation?.minimumInstallmentAmount ?? 0))

                            if (!`${value ?? ""}`.length) {
                                return createError({ path, message: 'Amount is required' })
                            }
                            if (Number.isNaN(Number(value))) {
                                return createError({ path, message: 'Invalid number' })
                            }
                            if (Number(value) < Number(minimumInstallmentAmount ?? 0)) {
                                return createError({
                                    path, message: `Minimum amount should be ${String.fromCharCode(parseInt(currencySymbol, 16))} ${Number(minimumInstallmentAmount ?? 0)}`
                                })
                            }

                            return true
                        } else {
                            if (!`${value ?? ""}`.length) {
                                return createError({ path, message: 'Units is required' })
                            }
                            if (Number.isNaN(Number(value))) {
                                return createError({ path, message: 'Invalid number' })
                            }
                            const decimalPart = `${value ?? ""}`.split('.')[1]
                            if (decimalPart && decimalPart.length > 3) {
                                return createError({ path, message: 'Maximum 3 digits are allowed after decimal point' })
                            }
                            if (Number(value) > Number(parent?.outgoingSchDetails?.availableUnits ?? 0)) {
                                return createError({ path, message: `Maximum ${parent?.outgoingSchDetails?.availableUnits ?? 0} units are available` })
                            }

                            return true
                        }
                    }),
                entireBalance: Yup
                    .boolean()
                    .notRequired(),
            })
        )
    }),
    ...((
        ENABLE_MF_TRANSACTIONS_POA_DOCUMENT_UPLOAD &&
        isRMLikeRole(role) &&
        MPOWER_ACCOUNT_TYPE_MASTER[selectedPortfolioObj?.portfolioAccountType?.toUpperCase()] ===
        MPOWER_ACCOUNT_TYPE_MASTER["POA"]
    ) ? {
        documents: Yup.array().of(
            Yup.object().shape({
                documentId: Yup.string(),
                documentType: Yup.string()
            })
        )
    } : {})
})


export const stpSchema = (currencySymbol: string, role: string, selectedPortfolioObj: Record<string, any>) => Yup.object().shape({
    fund: Yup.lazy(() => {
        return Yup.array().of(
            Yup.object().shape({
                frequency: Yup
                    .string()
                    .required('Frequency is Required'),
                amount: Yup
                    .number()
                    .required('Amount is Required')
                    .test('amount-frequency-validation', function (value, context) {
                        const { parent, createError, path } = context;

                        function getMinInstallmentAmount(selectedFreq: string) {
                            const outSchValArr = parent?.outgoingSchInvDetails?.validations?.filter((item: any) => item?.transactionType === 'STP' && item.frequency)

                            const inSchValArr = parent?.incomingSchInvDetails?.validations?.filter((item: any) => item?.transactionType === 'STP' && item.frequency)

                            const inSchValidation = inSchValArr?.find((item: any) => `${item?.frequency ?? ""}`.toUpperCase() === selectedFreq)

                            const outSchValidation = outSchValArr?.find((item: any) => `${item?.frequency ?? ""}`.toUpperCase() === selectedFreq)

                            const minInstallmentAmtInSch = Number(inSchValidation?.minimumInstallmentAmount ?? 0)

                            const minInstallmentAmtOutSch = Number(outSchValidation?.minimumInstallmentAmountOut ?? 0)

                            return Math.max(minInstallmentAmtInSch, minInstallmentAmtOutSch)
                        }

                        const frequencyMinAmounts: {
                            WEEKLY: number;
                            MONTHLY: number;
                            QUARTERLY: number;
                            YEARLY: number;
                            DAILY: number;
                            WEEKLYDAYS: number;
                        } = {
                            WEEKLY: getMinInstallmentAmount('WEEKLY'),
                            MONTHLY: getMinInstallmentAmount('MONTHLY'),
                            QUARTERLY: getMinInstallmentAmount('QUARTERLY'),
                            YEARLY: getMinInstallmentAmount('YEARLY'),
                            DAILY: getMinInstallmentAmount('DAILY'),
                            WEEKLYDAYS: getMinInstallmentAmount('WEEKLYDAYS'),
                        };

                        const selectedFrequency = `${parent?.frequency ?? ""}`.toUpperCase();

                        if (Number(value) < Number(frequencyMinAmounts[selectedFrequency as keyof typeof frequencyMinAmounts] ?? 0)) {
                            return createError({
                                path,
                                message: `Minimum amount should be ${String.fromCharCode(parseInt(currencySymbol, 16))} ${Number(frequencyMinAmounts[selectedFrequency as keyof typeof frequencyMinAmounts] ?? 0)}`,
                            });
                        }

                        return true
                    }),
                swpDate: Yup
                    .date()
                    .typeError('Invalid Date')
                    .required('STP Date is Required')
                    .min(dayjs().add(-1, 'day'), 'Past date are not allowed')
                    .test('frequency', (value, context) => {
                        const { path, createError, parent } = context

                        const stpValidationArr = parent?.incomingSchInvDetails?.validations?.filter((item: any) => item?.transactionType === 'STP' && item.frequency)

                        const commonValidation = (frequency: string) => stpValidationArr?.find((item: any) => item?.frequency === frequency)

                        // For past date
                        if ((dayjs(new Date()).isAfter(value, 'day'))) {
                            return createError({ path, message: 'Past date is not allowed' });
                        }
                        // For T+1 date
                        if ((dayjs(new Date()).add(1, 'day').isAfter(value, 'day'))) {
                            return createError({ path, message: 'Date should be T+1' });
                        }
                        const validation = commonValidation(parent?.frequency ?? "")
                        const valid = handleDateValidation({
                            ArrayOfDate: validation?.investmentDates,
                            date: dayjs(value),
                            weekDaysAllowed: parent?.incomingSchWeekDays?.STP_WEEKDAYS ?? [],
                            frequency: parent?.frequency,
                            isStp: true
                        })
                        if (valid) {
                            return createError({
                                message: `Only ${parent?.frequency === "WEEKLY" ? (parent?.incomingSchWeekDays?.STP_WEEKDAYS ?? []).join(', ') : validation?.investmentDates.join(', ')} are allowed`,
                                path
                            })
                        }
                        return true
                    }),
                installment: Yup
                    .number()
                    .test('installment-frequency-validation', function (value, context) {
                        const { parent, createError, path } = context;

                        const stpValidationArr = parent?.incomingSchInvDetails?.validations?.filter((item: any) => item?.transactionType === 'STP' && item.frequency)


                        const daily = stpValidationArr?.find((item: any) => `${item?.frequency ?? ""}`.toUpperCase() === 'DAILY')

                        const weekly = stpValidationArr?.find((item: any) => `${item?.frequency ?? ""}`.toUpperCase() === 'WEEKLY')

                        const weeklyDays = stpValidationArr?.find((item: any) => `${item?.frequency ?? ""}`.toUpperCase() === 'WEEKLYDAYS')

                        const monthly = stpValidationArr?.find((item: any) => `${item?.frequency ?? ""}`.toUpperCase() === 'MONTHLY')

                        const quarterly = stpValidationArr?.find((item: any) => `${item?.frequency ?? ""}`.toUpperCase() === 'QUARTERLY')

                        const yearly = stpValidationArr?.find((item: any) => `${item?.frequency ?? ""}`.toUpperCase() === 'YEARLY')

                        if (`${parent?.frequency ?? ""}`.toUpperCase() === 'WEEKLY') {
                            if (!`${value ?? ""}`.length) {
                                return createError({ path, message: 'Installment is Required' })
                            }

                            if (Number(value) < Number(weekly?.minimumInstallments ?? 0)) {
                                return createError({
                                    path, message: `Minimum installments should be ${Number(weekly?.minimumInstallments ?? 0)}`
                                })
                            }
                        }

                        if (`${parent?.frequency ?? ""}`.toUpperCase() === 'MONTHLY') {
                            if (!`${value ?? ""}`.length) {
                                return createError({ path, message: 'Installment is Required' })
                            }

                            if (Number(value) < Number(monthly?.minimumInstallments ?? 0)) {
                                return createError({
                                    path, message: `Minimum installments should be ${Number(monthly?.minimumInstallments ?? 0)}`
                                })
                            }
                        }

                        if (`${parent?.frequency ?? ""}`.toUpperCase() === 'QUARTERLY') {
                            if (!`${value ?? ""}`.length) {
                                return createError({ path, message: 'Installment is Required' })
                            }

                            if (Number(value) < Number(quarterly?.minimumInstallments ?? 0)) {
                                return createError({
                                    path, message: `Minimum installments should be ${Number(quarterly?.minimumInstallments ?? 0)}`
                                })
                            }
                        }

                        if (`${parent?.frequency ?? ""}`.toUpperCase() === 'YEARLY') {
                            if (!`${value ?? ""}`.length) {
                                return createError({ path, message: 'Installment is Required' })
                            }

                            if (Number(value) < Number(yearly?.minimumInstallments ?? 0)) {
                                return createError({
                                    path, message: `Minimum installments should be ${Number(yearly?.minimumInstallments ?? 0)}`
                                })
                            }
                        }

                        if (`${parent?.frequency ?? ""}`.toUpperCase() === 'DAILY') {
                            return true
                        }

                        if (`${parent?.frequency ?? ""}`.toUpperCase() === 'WEEKLYDAYS') {
                            if (!`${value ?? ""}`.length) {
                                return createError({ path, message: 'Installment is Required' })
                            }

                            if (Number(value) < Number(weeklyDays?.minimumInstallments ?? 0)) {
                                return createError({
                                    path, message: `Minimum installments should be ${Number(weeklyDays?.minimumInstallments ?? 0)}`
                                })
                            }
                        }

                        return true
                    }),
                stpEndDate: Yup
                    .date()
                    .typeError('Invalid Date')
                    .test('stp-end-date-check', (value, context) => {
                        const { parent, path, createError } = context

                        if (parent?.frequency === 'DAILY') {
                            if (!value) {
                                return createError({
                                    message: 'STP End Date is Required',
                                    path,
                                });
                            }
                            if (parent?.swpDate && value && dayjs(value)?.isBefore(dayjs(parent?.swpDate))) {
                                return createError({
                                    message: 'End date should be greater than start date',
                                    path
                                })
                            }

                            return true
                        }
                        return true
                    })
            }))
    }),
    ...((
        ENABLE_MF_TRANSACTIONS_POA_DOCUMENT_UPLOAD &&
        isRMLikeRole(role) &&
        MPOWER_ACCOUNT_TYPE_MASTER[selectedPortfolioObj?.portfolioAccountType?.toUpperCase()] ===
        MPOWER_ACCOUNT_TYPE_MASTER["POA"]
    ) ? {
        documents: Yup.array().of(
            Yup.object().shape({
                documentId: Yup.string(),
                documentType: Yup.string()
            })
        )
    } : {})
})
